import { Component, inject, signal } from '@angular/core';
import { form, FormField, FormRoot, min, submit } from '@angular/forms/signals';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Currency } from '@app/shared/types/enums/currency.enum';
import { QuantityUnits } from '@app/shared/types/enums/quantity-units.enum';
import { TimeUnits } from '@app/shared/types/enums/time-units.enum';
import { DropdownItem } from '@app/shared/types/models/shared/dropdown-item';
import { TaskFinalCost } from '@app/shared/types/models/task/task-final-cost';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { amountAndUnitToDuration, durationToHours } from '@app/shared/utils/time-utils';
import { TranslatePipe } from '@ngx-translate/core';

interface FinalCostFormModel {
  time: { amount: number | null; units: TimeUnits };
  financial: { amount: number | null; currency: Currency };
  quantity: { amount: number | null; units: QuantityUnits };
}

function buildInitialModel(finalCost?: TaskFinalCost): FinalCostFormModel {
  const model: FinalCostFormModel = {
    time: { amount: null, units: TimeUnits.HOURS },
    financial: { amount: null, currency: Currency.NOK },
    quantity: { amount: null, units: QuantityUnits.PIECES }
  };

  if (finalCost?.time) {
    model.time = {
      amount: durationToHours(finalCost.time),
      units: TimeUnits.HOURS
    };
  }

  if (finalCost?.financial?.currency) {
    model.financial = {
      amount: finalCost.financial.amount,
      currency: finalCost.financial.currency
    };
  }

  if (finalCost?.quantity?.unit) {
    model.quantity = {
      amount: finalCost.quantity.amount,
      units: finalCost.quantity.unit
    };
  }

  return model;
}

export interface FinalCostDialogData {
  finalCost?: TaskFinalCost;
  autoFocus: boolean;
}

@Component({
  selector: 'proffeo-final-cost-dialog',
  templateUrl: './final-cost-dialog.component.html',
  imports: [
    FormField,
    FormRoot,
    TranslatePipe,
    MatDialogModule,
    MatIconModule,
    UiButtonComponent,
    UiInputComponent,
    UiSelectComponent,
    UiDialogComponent
  ]
})
export class FinalCostDialogComponent {
  private readonly dialogRef: MatDialogRef<FinalCostDialogComponent> = inject(MatDialogRef<FinalCostDialogComponent>);
  private readonly modalData: FinalCostDialogData = inject(MAT_DIALOG_DATA);

  protected readonly finalCostModel = signal<FinalCostFormModel>(buildInitialModel(this.modalData.finalCost));

  protected readonly finalCostForm = form(this.finalCostModel, path => {
    min(path.time.amount, 0);
    min(path.financial.amount, 0);
    min(path.quantity.amount, 0);
  });

  protected readonly timeUnitOptions: DropdownItem<TimeUnits>[] = [
    { label: 'time-units.hours', value: TimeUnits.HOURS },
    { label: 'time-units.minutes', value: TimeUnits.MINUTES },
    { label: 'time-units.man-days', value: TimeUnits.MAN_DAYS }
  ];

  protected readonly currencyOptions: DropdownItem<Currency>[] = [
    { label: 'NOK', value: Currency.NOK },
    { label: 'PLN', value: Currency.PLN },
    { label: 'EUR', value: Currency.EUR },
    { label: 'USD', value: Currency.USD }
  ];

  protected readonly quantityUnitOptions: DropdownItem<QuantityUnits>[] = [
    { label: 'quantity-units.pieces', value: QuantityUnits.PIECES },
    { label: 'quantity-units.meters', value: QuantityUnits.METERS },
    { label: 'quantity-units.meters-square', value: QuantityUnits.METERS_SQUARE },
    { label: 'quantity-units.meters-cubic', value: QuantityUnits.METERS_CUBIC },
    { label: 'quantity-units.sets', value: QuantityUnits.SETS }
  ];

  protected saveFinalCost(): void {
    void submit(this.finalCostForm, {
      action: async () => {
        const { amount: timeAmount, units } = this.finalCostModel().time;
        const { amount: costAmount, currency } = this.finalCostModel().financial;
        const { amount: quantityAmount, units: quantityUnits } = this.finalCostModel().quantity;

        const result: TaskFinalCost = {
          time: amountAndUnitToDuration(timeAmount, units) ?? null,
          financial: { amount: costAmount, currency },
          quantity: { amount: quantityAmount, unit: quantityUnits }
        };

        this.dialogRef.close(result);
      }
    });
  }

  protected cancelFinalCost(): void {
    this.dialogRef.close();
  }
}
